import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSites } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import BatchActionsDrawer from "@/components/BatchActionsDrawer";
import BatchProgressPanel, {
  BATCH_STATUS_LABELS, BATCH_STATUS_STYLES, OPERATION_LABELS, formatDate,
} from "@/components/BatchProgressPanel";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Languages, Layers, Loader2, Plus, Trash2 } from "lucide-react";

export const PARENT_STATUS_STYLES = {
  DRAFT: "bg-slate-100 text-slate-600",
  PARTIAL: "bg-amber-100 text-amber-700",
  READY: "bg-blue-100 text-blue-700",
  PARTIALLY_PUBLISHED: "bg-violet-100 text-violet-700",
  PUBLISHED: "bg-emerald-100 text-emerald-700",
  ERROR: "bg-red-100 text-red-700",
};

export const LOCALE_STATUS_STYLES = {
  NOT_GENERATED: "bg-slate-100 text-slate-500",
  GENERATING: "bg-blue-100 text-blue-700 animate-pulse",
  DRAFT: "bg-amber-50 text-amber-700 border border-amber-200",
  READY_TO_PUBLISH: "bg-blue-50 text-blue-700 border border-blue-200",
  PUBLISHING: "bg-blue-100 text-blue-700 animate-pulse",
  PUBLISHED: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  MANUAL_REQUIRED: "bg-orange-50 text-orange-700 border border-orange-200",
  ERROR: "bg-red-50 text-red-700 border border-red-200",
};

export default function ContentParents() {
  const { activeSite } = useSites();
  const { user } = useAuth();
  const readOnly = user?.workspace_role === "viewer";
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [watchBatchId, setWatchBatchId] = useState(null);
  const siteIdRef = useRef(null);
  const justLaunchedRef = useRef(false);

  const load = useCallback(async () => {
    if (!activeSite) return;
    setLoading(true);
    try {
      const { data } = await api.get("/content-parents", { params: { site_id: activeSite.id } });
      setParents(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [activeSite]);

  const loadRecent = useCallback(async () => {
    if (!activeSite) return [];
    try {
      const { data } = await api.get("/content-batches", { params: { site_id: activeSite.id } });
      setRecent(data || []);
      return data || [];
    } catch {
      return [];
    }
  }, [activeSite]);

  useEffect(() => { load(); }, [load]);

  /* Changement de site : sélection/lot suivi remis à zéro (batch = site courant seul).
     Rechargement navigateur : on reprend l'AFFICHAGE d'un lot CREATED/RUNNING existant —
     jamais de nouveau lot créé au reload (le backend poursuit seul). */
  useEffect(() => {
    if (!activeSite) return;
    if (siteIdRef.current === activeSite.id) return;
    siteIdRef.current = activeSite.id;
    setSelected(new Set());
    setWatchBatchId(null);
    setDrawerOpen(false);
    loadRecent().then((batches) => {
      const active = (batches || []).find((b) => b.status === "CREATED" || b.status === "RUNNING");
      if (active) {
        setWatchBatchId(active.id);
        setRecentOpen(true);
      }
    });
  }, [activeSite, loadRecent]);

  const selectedParents = useMemo(
    () => parents.filter((p) => selected.has(p.id)), [parents, selected]);
  const parentsById = useMemo(
    () => Object.fromEntries(parents.map((p) => [p.id, p])), [parents]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = parents.length > 0 && parents.every((p) => selected.has(p.id));
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(parents.map((p) => p.id)));
  };

  /* Au launch : la sélection est CONSERVÉE tant que le drawer est ouvert — la vider ici
     recréerait le tableau `parents` du drawer et détruirait le panneau de progression
     (bug HIGH iteration_34). Elle est vidée à la fermeture du drawer. */
  const onLaunched = useCallback((batch) => {
    justLaunchedRef.current = true;
    setWatchBatchId(batch.id);
    setRecentOpen(true);
    loadRecent();
  }, [loadRecent]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    if (justLaunchedRef.current) {
      justLaunchedRef.current = false;
      setSelected(new Set());
    }
  }, []);

  const onBatchTerminal = useCallback(() => { loadRecent(); load(); }, [loadRecent, load]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce contenu et toutes ses variantes linguistiques ?")) return;
    try {
      await api.delete(`/content-parents/${id}`);
      toast.success("Contenu supprimé");
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <PageHeader
        overline={activeSite ? `Contenu multilingue · ${activeSite.name || activeSite.label}` : "Contenu multilingue"}
        title="Contenus multilingues"
        description="1 sujet = 1 contenu parent + une variante SEO localisée par langue du site."
      />
      <div className="flex justify-end mb-4">
        {!readOnly && (
          <Link to="/generator" data-testid="ml-new-content-btn"
            className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#001D6B] text-white px-4 py-2 rounded-md text-sm font-medium">
            <Plus className="w-4 h-4" /> Nouveau contenu
          </Link>
        )}
      </div>

      {!readOnly && selected.size > 0 && (
        <div data-testid="content-batch-actions"
          className="sticky top-2 z-10 mb-4 flex items-center justify-between gap-3 bg-[#002FA7] text-white rounded-md px-4 py-2.5 shadow-md flex-wrap">
          <span className="text-sm font-medium">
            Actions en lot — {selected.size} contenu{selected.size > 1 ? "s" : ""} sélectionné{selected.size > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} data-testid="content-batch-clear-selection"
              className="text-xs text-blue-100 hover:text-white underline">
              Tout désélectionner
            </button>
            <button onClick={() => setDrawerOpen(true)} data-testid="content-batch-open-drawer"
              className="inline-flex items-center gap-1.5 bg-white text-[#002FA7] text-sm font-semibold px-3 py-1.5 rounded-md hover:bg-blue-50">
              <Layers className="w-4 h-4" /> Générer / régénérer
            </button>
          </div>
        </div>
      )}

      {!activeSite ? (
        <div className="border border-dashed border-slate-300 bg-white rounded-md p-10 text-center text-sm text-slate-600">
          Sélectionnez d&apos;abord un site.
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-8"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
      ) : parents.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white rounded-md p-10 text-center" data-testid="ml-empty-state">
          <Languages className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">Aucun contenu multilingue — créez-en un depuis le Générateur IA en sélectionnant les langues du site.</p>
        </div>
      ) : (
        <>
          {!readOnly && (
            <label className="flex items-center gap-2 text-xs text-slate-500 mb-2 cursor-pointer w-fit">
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                data-testid="content-batch-select-all" aria-label="Tout sélectionner"
                className="rounded border-slate-300" />
              Tout sélectionner ({parents.length})
            </label>
          )}
          <div className="space-y-3" data-testid="ml-parents-list">
            {parents.map((p) => (
              <div key={p.id} className="border border-slate-200 bg-white rounded-md p-4 flex items-center gap-4" data-testid={`ml-parent-${p.id}`}>
                {!readOnly && (
                  <input type="checkbox" checked={selected.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    data-testid={`content-batch-checkbox-${p.id}`}
                    aria-label={`Sélectionner ${p.topic}`}
                    className="rounded border-slate-300 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/content/parents/${p.id}`} className="text-sm font-semibold text-slate-900 hover:text-[#002FA7] truncate block">
                    {p.topic}
                  </Link>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${PARENT_STATUS_STYLES[p.status] || "bg-slate-100 text-slate-600"}`} data-testid={`ml-parent-status-${p.id}`}>
                      {p.status}
                    </span>
                    {(p.locales || []).map((l) => (
                      <span key={l.locale} className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${LOCALE_STATUS_STYLES[l.status] || "bg-slate-100"}`} title={l.status}>
                        {l.locale}
                      </span>
                    ))}
                    <span className="text-[10px] text-slate-400 ml-1">{p.target_zone || ""}</span>
                  </div>
                </div>
                {!readOnly && (
                  <button onClick={() => remove(p.id)} data-testid={`ml-parent-delete-${p.id}`}
                    className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {activeSite && (
        <div className="mt-8 border border-slate-200 bg-white rounded-md" data-testid="content-batch-recent">
          <button onClick={() => { const next = !recentOpen; setRecentOpen(next); if (next) loadRecent(); }}
            data-testid="content-batch-recent-toggle" aria-expanded={recentOpen}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800">
            <span className="inline-flex items-center gap-2">
              <Layers className="w-4 h-4 text-slate-400" /> Lots récents
            </span>
            {recentOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          </button>
          {recentOpen && (
            <div className="border-t border-slate-100 p-4 space-y-3">
              {recent.length === 0 ? (
                <p className="text-xs text-slate-500" data-testid="content-batch-recent-empty">
                  Aucun lot pour ce site.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {recent.map((b) => (
                    <button key={b.id} onClick={() => setWatchBatchId(b.id)}
                      data-testid={`content-batch-recent-item-${b.id}`}
                      className={`w-full flex items-center gap-3 text-left text-xs border rounded-md px-3 py-2 hover:border-slate-300 ${watchBatchId === b.id ? "border-[#002FA7]/40 bg-blue-50/40" : "border-slate-100"}`}>
                      <span className="text-slate-400 shrink-0">{formatDate(b.created_at)}</span>
                      <span className="text-slate-700 font-medium truncate flex-1 min-w-0">
                        {OPERATION_LABELS[b.operation_type] || b.operation_type}
                      </span>
                      <span className="text-slate-500 shrink-0">
                        {b.done ?? 0}/{b.total ?? 0}
                        {" · "}{b.counts?.success ?? 0} réussi{(b.counts?.success ?? 0) > 1 ? "s" : ""}
                        {" · "}{b.counts?.error ?? 0} erreur{(b.counts?.error ?? 0) > 1 ? "s" : ""}
                        {" · "}{b.counts?.skipped ?? 0} ignoré{(b.counts?.skipped ?? 0) > 1 ? "s" : ""}
                        {(b.counts?.interrupted ?? 0) > 0 ? ` · ${b.counts.interrupted} interrompu${b.counts.interrupted > 1 ? "s" : ""}` : ""}
                      </span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${BATCH_STATUS_STYLES[b.status] || "bg-slate-100 text-slate-600"}`}>
                        {BATCH_STATUS_LABELS[b.status] || b.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {watchBatchId && !drawerOpen && (
                <div className="border-t border-slate-100 pt-3">
                  <BatchProgressPanel batchId={watchBatchId} parentsById={parentsById}
                    readOnly={readOnly} onTerminal={onBatchTerminal}
                    onRetryLaunched={(b) => { setWatchBatchId(b.id); loadRecent(); }} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!readOnly && (
        <BatchActionsDrawer open={drawerOpen} onClose={closeDrawer}
          site={activeSite} parents={selectedParents} onLaunched={onLaunched} />
      )}
    </div>
  );
}
